// ===== Packages =====

import {
    useEffect,
    useRef,
}                                                           from 'react';

// ===== Services =====

import recordUserAction, {
    hasRecentErrorLog,
}                                                           from '../services/recordUserAction';

// ===== Interfaces =====

import {
    IUseFailureSnackbarLoggerParams,
}                                                           from '../interfaces';

// ===== Enums =====

import {
    FAILURE_TELEMETRY_TYPE,
    USER_ACTION_TYPE,
}                                                           from '../enums';

// ===== General Constants =====

import {
    FAILURE_SNACKBAR_LOG_DEDUPE_WINDOW_MS,
}                                                           from '../constants/generalConstants';

const useFailureSnackbarLogger = ({
    test,
    sessionId,
    snackbarData,
    source,
    context,
    dedupeWindowMs = FAILURE_SNACKBAR_LOG_DEDUPE_WINDOW_MS,
}: IUseFailureSnackbarLoggerParams): void => {
    const previousLogRef = useRef<{
        signature: string,
        timestamp: number,
    } | null>(null);
    let contextPayloadSignature = '';
    try {
        contextPayloadSignature = JSON.stringify(context.payload);
    } catch {
        contextPayloadSignature = '[unserializable-payload]';
    }

    useEffect(() => {
        if (
            !sessionId
            || !snackbarData?.visible
            || !snackbarData?.hasFailure
            || !snackbarData?.text
        ) {
            return;
        }

        const signature = `${context.signature}:${snackbarData.text}`;
        const now = Date.now();
        const previousLog = previousLogRef.current;
        if (
            previousLog
            && previousLog.signature === signature
            && now - previousLog.timestamp < dedupeWindowMs
        ) {
            return;
        }
        if (hasRecentErrorLog({
            sessionId,
            message: snackbarData.text,
            windowMs: dedupeWindowMs,
        })) {
            return;
        }

        previousLogRef.current = {
            signature,
            timestamp: now,
        };

        // Failure-styled snackbars are guidance by default; operational errors
        // can opt into unexpected telemetry at the source branch.
        const failureTelemetryType = snackbarData.failureTelemetryType
            ?? FAILURE_TELEMETRY_TYPE.userCorrectable;

        recordUserAction({
            test,
            type: USER_ACTION_TYPE.error,
            sessionId,
            payload: {
                error: snackbarData.text,
                failureTelemetryType,
                source,
                ...context.payload,
            },
        });
    }, [
        contextPayloadSignature,
        context.signature,
        dedupeWindowMs,
        snackbarData?.failureTelemetryType,
        sessionId,
        snackbarData?.hasFailure,
        snackbarData?.text,
        snackbarData?.visible,
        source,
        test,
    ]);
};

export default useFailureSnackbarLogger;
